import React from 'react'
import { CheckMarkIcon } from '@mote/assets/icons'
import { preventBubblingEvent } from '@mote/helpers/browser'
import {
  TRANSCRIPTION_LANGUAGES,
  TRANSLATION_LANGUAGES
} from '@mote/helpers/constants'
import { ariaAttr, dataAttr } from '@mote/helpers/dom'
import type { DialogProps } from '../DialogV2'
import { Dialog } from '../DialogV2'
import {
  TranslationLanguageItem,
  getSizableStyles
} from './TranscriptTranslationPicker.styles'
import type { Size, Variant } from './types'

const sortingComparator = (a: string[], b: string[]): number => {
  if (a[1] < b[1]) return -1
  if (a[1] > b[1]) return 1
  return 0
}

export function getOptions(v: Variant) {
  if (v === 'transcription') {
    return TRANSCRIPTION_LANGUAGES
  }

  return TRANSLATION_LANGUAGES.sort(sortingComparator)
}

export type TranslationLanguage = { code: string; name: string }

function getDynamicContent(v: Variant, size: Size) {
  const styleProps = getSizableStyles(size)

  if (v === 'transcription') {
    return ["I'm speaking", styleProps] as const
  }

  return ['Transcription language', styleProps] as const
}

export interface TranscriptTranslationPickerProps
  extends Omit<DialogProps, 'children'> {
  isOpen: boolean
  value: string
  numberOfVisibleTranslations?: number
  onClose: () => void
  onChange: (value: TranslationLanguage) => Promise<any> | void
  variant?: Variant
  size?: Size
}

export const TranscriptTranslationPicker = (
  props: TranscriptTranslationPickerProps
): JSX.Element => {
  const language =
    !props.value && props.variant === 'transcription' ? 'en-US' : props.value
  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLLIElement>) => {
    switch (event.key) {
      case ' ':
      case 'Enter':
        preventBubblingEvent(event)
        event.currentTarget.click()
        break
      default:
        break
    }
  }

  const handleOnKeyUp = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case ' ':
        event.preventDefault()
        break
      default:
        break
    }
  }

  const [title, styleProps] = getDynamicContent(
    props.variant || 'translation',
    props.size || 'lg'
  )

  return (
    <Dialog
      isOpen={props.isOpen}
      onClose={props.onClose}
      portalProps={props.portalProps}
      closeButtonProps={styleProps.closeButton}
      maxWidth={props.maxWidth}
      rootProps={props.rootProps}
      scrollBehavior='inside'
      useAbsolutePosition={props.useAbsolutePosition}
      blockScrollOnMount={props.blockScrollOnMount}
    >
      <Dialog.Header {...styleProps.header}>{title}</Dialog.Header>
      <Dialog.Content
        as='ul'
        {...styleProps.content}
        m={0}
        style={{ scrollSnapType: 'y proximity' }}
      >
        {getOptions(props.variant || 'translation').map(([code, name]) => {
          const isSelected = code === language

          return (
            <TranslationLanguageItem
              key={code}
              role='menuitemradio'
              aria-label={name}
              tabIndex={0}
              onKeyDown={!isSelected ? handleOnKeyDown : undefined}
              onKeyUp={!isSelected ? handleOnKeyUp : undefined}
              onClick={
                !isSelected
                  ? () => {
                      props.onChange({ code, name })
                    }
                  : undefined
              }
              data-lang-code={code}
              data-selected={dataAttr(isSelected)}
              aria-checked={ariaAttr(isSelected)}
              {...styleProps.listItem}
            >
              {name}
              {isSelected && (
                <CheckMarkIcon color='primary.base' w='14rpx' h='14rpx' />
              )}
            </TranslationLanguageItem>
          )
        })}
      </Dialog.Content>
      <Dialog.Footer {...styleProps.footer} />
    </Dialog>
  )
}
